import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { UserProfileService } from "app/bundles/authorize/shared/user-profile.service";
import { IconService } from "app/bundles/requests/shared/services/icon.service";
import { MapService } from "app/bundles/requests/shared/services/map.service";
import { UtilService } from "app/bundles/requests/shared/services/util.service";
import { ConfirmDialogService } from "app/core/confirm-dialog.service";
import { CoreService } from "app/core/core.service";
import { StorageService } from "app/shared/services/storage.service";
import { Subscription, of, switchMap, tap } from "rxjs";
import { CollapseItemsEnum } from "../shared/models/collapseItem";
import {
  IWorkorderOrServiceRequestSummary,
  RequestSourceTypeEnum,
} from "../shared/models/request";
import {
  IBatchMode,
  RequestInBatchService,
} from "../shared/services/request-in-batch.service";
import { RequestService } from "../shared/services/request.service";

@Component({
  selector: "app-request",
  templateUrl: "./request.component.html",
  styleUrls: ["./request.component.scss"],
})
export class RequestComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() request: IWorkorderOrServiceRequestSummary;
  @Input() isSelectedRequest: boolean;
  @Input() popupMode = false;
  @ViewChild("requestCard", { static: false }) requestCard: ElementRef;
  @Input() isEditMode = true;
  @Output() public selectedRequestChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  private subscriptions = new Subscription();
  public borderStyle: any;
  public className: any = "";
  public delay = 0;
  public requestDivId: string;
  public responsibleUserToCompare: string;
  public batchMode: IBatchMode;
  public hasDelayOver1Day = false;
  public hasDelayWithin1Day = false;

  constructor(
    private coreService: CoreService,
    private router: Router,
    private iconService: IconService,
    private mapService: MapService,
    private storageService: StorageService,
    private utilService: UtilService,
    private userProfileService: UserProfileService,
    private confirmDialogService: ConfirmDialogService,
    private requestService: RequestService,
    private requestInBatchService: RequestInBatchService,
  ) {}

  public ngOnInit() {
    this.requestDivId =
      (!this.isEditMode ? "P_" : "") + this.request.id.toString();
    this.className = this.getClassName();

    this.delay = this.utilService.getDelay(
      this.request.estimatedCompletionDate,
    );

    if (this.delay) {
      this.hasDelayOver1Day = this.utilService.hasDelayOver1Day(this.delay);
      this.hasDelayWithin1Day = this.utilService.hasDelayWithin1Day(this.delay);
    }
    if (this.isEditMode) {
      this.subscriptions.add(
        this.coreService.zoomMarker$.subscribe((res) => {
          if (res.id == this.request.id) {
            this.request.isSelected = res.isSelected;
            this.className = this.getClassName();
          }
        }),
      );

      this.subscriptions.add(
        this.coreService.currentSelectedRequest$.subscribe(
          (request: IWorkorderOrServiceRequestSummary) => {
            /* Add for mono selection */
            if (request.id != this.request.id) {
              this.request.isSelected = false;
              this.className = this.getClassName();
            }
          },
        ),
      );
    }
    this.className = this.getClassName();
    this.subscriptions.add(
      this.userProfileService.userProfile$.subscribe(
        (user) => (this.responsibleUserToCompare = user.userName.toUpperCase()),
      ),
    );
    this.subscriptions.add(
      this.requestInBatchService.batchMode$.subscribe((batchMode) => {
        this.batchMode = batchMode;
        this.className = this.getClassName();
      }),
    );
  }

  public ngAfterViewInit(): void {
    if (this.request && this.popupMode) {
      const popupElement: any = document.createElement("div");
      popupElement.innerHTML = this.requestCard.nativeElement.innerHTML;

      // close button on popup
      const closeRequestButton =
        popupElement.getElementsByClassName("closeRequestButton")[0];
      closeRequestButton.addEventListener("click", () => this.closeWorkorder());

      // view button on popup
      const viewRequestButton =
        popupElement.getElementsByClassName("viewRequestButton")[0];
      viewRequestButton.addEventListener("click", () =>
        this.router.navigate(["view-request", this.request.id], {
          state: {
            requestSummary: this.request,
          },
        }),
      );

      // view button on popup
      const editRequestButton =
        popupElement.getElementsByClassName("editRequestButton")[0];
      editRequestButton.addEventListener("click", () =>
        this.router.navigate(["edit-request", this.request.id], {
          state: {
            requestSummary: this.request,
          },
        }),
      );

      this.mapService.showPopup(popupElement);
    }
  }

  public ngOnChanges(): void {
    if (this.isSelectedRequest) {
      this.request.isSelected = true;
      this.selectedRequestChange.emit(true);

      this.onSelectRequest(false);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public getClassName(): string {
    let className = "request--normal";

    if (this.popupMode) {
      return "request--popup";
    }

    if (this.request.isSelected && this.batchMode && this.batchMode.isActive) {
      return "request--selected";
    }

    if (this.request.isSelected && this.batchMode && !this.batchMode.isActive) {
      return "request--localized";
    }

    if (this.delay > 0 || this.request.isReactivated) {
      className = "request--delayed";
    }

    if (!this.isEditMode) {
      className = className + " request--height";
    }
    return className;
  }

  public closeWorkorder() {
    if (this.request.source === RequestSourceTypeEnum.workorderRequest) {
      void this.router.navigate(["/edit-request", this.request.id], {
        state: {
          requestSummary: this.request,
          actionFromMenu: CollapseItemsEnum.closeWorkorder,
        },
      });
    }
  }

  public onViewRequest() {
    this.mapService.saveMapStatus();
    if (!this.storageService.getTrackedRequestId()) {
      this.storageService.savePositionList(this.request.id.toString());
    }

    void this.router.navigate(["/view-request", this.request.id], {
      state: {
        requestSummary: this.request,
      },
    });
  }

  public onEditRequest() {
    this.mapService.saveMapStatus();
    if (!this.storageService.getTrackedRequestId()) {
      this.storageService.savePositionList(this.request.id.toString());
    }
    void this.router.navigate(["/edit-request", this.request.id], {
      state: {
        requestSummary: this.request,
      },
    });
  }

  public onSelectRequest(toggleSelection = true) {
    this.coreService.startSpinner();
    this.mapService.moveZoomProgrammatic(true);

    if (toggleSelection) {
      this.request.isSelected = !this.request.isSelected;
    }
    this.className = this.getClassName();
    this.utilService.resetSelection(this.storageService.getGeoJsonRequest());
    this.utilService.updateSelection(
      this.storageService.getGeoJsonRequest(),
      this.request,
    );
    this.coreService.notifyRequestSelectionFromList(this.request);
    this.coreService.notifyCurrentSelectedRequest(this.request);
    if (this.request.isSelected) {
      this.coreService.notifyCenterOnMarker({
        option: "centerOnMarker",
        value: this.request.geometry.coordinates,
      });
      this.storageService.enabledTracking(this.request.id.toString());
    } else {
      this.storageService.disabledTracking();
      this.mapService.setUserZoom();
    }

    this.mapService.moveZoomProgrammatic(false);
    this.coreService.stopSpinner();
  }

  public onSelectInBatch() {
    this.request.isSelected = !this.request.isSelected;
    this.className = this.getClassName();
    this.utilService.updateSelection(
      this.storageService.getGeoJsonRequest(),
      this.request,
    );
    if (this.request.isSelected) {
      this.requestInBatchService.addSelectedRequest(this.request);
    } else {
      this.requestInBatchService.removeSelectedRequest(this.request);
    }
    this.coreService.notifyRequestSelectionFromList(this.request);
  }

  public getIconName(serviceCode: string): string {
    return this.iconService.getIconName(serviceCode);
  }

  public onTakeInChargeHandle() {
    if (!this.storageService.getTrackedRequestId()) {
      this.storageService.savePositionList(this.request.id.toString());
    }

    if (this.request.source === RequestSourceTypeEnum.serviceRequest) {
      void this.router.navigate(["/edit-request", this.request.id], {
        state: {
          requestSummary: this.request,
          actionFromMenu: CollapseItemsEnum.takeInCharge,
        },
      });
    } else {
      this.confirmDialogService.createConfirmDialog({
        confirmedCB: () => {
          return of(this.request.id).pipe(
            tap(() => this.coreService.startSpinner()),
            switchMap((requestId) =>
              this.requestService.cancelTakeInCharge([requestId]),
            ),
            tap((res) =>
              this.storageService.savePositionList(
                res?.success?.[0]?.ddsId?.toString?.(),
              ),
            ),
            switchMap(() => this.requestService.getGeojsonObservable()),
            tap(() => {
              this.storageService.notifyComponents();
              this.storageService.notifyMap();
              this.storageService.notifyList();
            }),
            tap(() => this.coreService.stopSpinner()),
            tap(() => this.selectedRequestChange.emit(true)),
          );
        },
        bodyMessage:
          "components.confirmDialogComponent.cancelTakeInChargeMessage",
      });
    }
  }

  public isRequestInBatchDisabled(): boolean {
    return this.requestInBatchService.isRequestInBatchDisabled(this.request);
  }
}
